import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  BookOpen, HelpCircle, ClipboardCheck, Plus, Search, Pencil, Trash2, 
  Target, GripVertical, X, Save, Loader2, Paperclip, Download, Upload
} from 'lucide-react';
import { toast } from 'sonner';

import { ModulesPage } from './ModulesPage';
import { QCMPage } from './QCMPage';
import { saveBlob } from '../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EvalFichiersSection = ({ evalId }) => {
  const [fichiers, setFichiers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const fetchFichiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/evaluations-manuelles/${evalId}/fichiers`);
      setFichiers(res.data || []);
    } catch (err) { setFichiers([]); }
  }, [evalId]);

  useEffect(() => { fetchFichiers(); }, [fetchFichiers]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/evaluations-manuelles/${evalId}/fichiers`, fd);
      toast.success('Fichier joint');
      fetchFichiers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDownload = async (f) => {
    try {
      const res = await axios.get(`${API_URL}/evaluations-fichiers/${f.id}/download`, { responseType: 'blob' });
      saveBlob(res.data, f.filename);
    } catch (err) { toast.error('Erreur de téléchargement'); }
  };

  const handleDelete = async (f) => {
    try {
      await axios.delete(`${API_URL}/evaluations-fichiers/${f.id}`);
      toast.success('Fichier supprimé');
      fetchFichiers();
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-2 pt-3 border-t" data-testid="eval-fichiers-section">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4" /> Fichiers joints ({fichiers.length})</Label>
        <input type="file" ref={inputRef} className="hidden" onChange={handleUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp" />
        <Button variant="outline" size="sm" className="gap-1" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="btn-upload-eval-fichier">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Joindre un fichier
        </Button>
      </div>
      {fichiers.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Aucun fichier joint (sujet, corrigé, grille...)</p>
      ) : (
        <div className="space-y-1.5">
          {fichiers.map(f => (
            <div key={f.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2" data-testid={`eval-fichier-${f.id}`}>
              <div className="min-w-0 flex items-center gap-2">
                <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{f.filename}</p>
                  <p className="text-[10px] text-slate-400">{Math.round((f.taille || 0) / 1024)} Ko — {f.uploaded_by_nom}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(f)} data-testid={`download-eval-fichier-${f.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(f)} data-testid={`delete-eval-fichier-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EVAL_TYPES = [
  { value: 'exercice', label: 'Exercice', short: 'EXO', color: 'bg-blue-100 text-blue-700' },
  { value: 'cas_pratique', label: 'Cas pratique', short: 'CAS', color: 'bg-amber-100 text-amber-700' },
  { value: 'application', label: 'Application', short: 'APP', color: 'bg-purple-100 text-purple-700' },
  { value: 'examen', label: 'Examen', short: 'EXA', color: 'bg-red-100 text-red-700' },
  { value: 'atelier', label: 'Atelier', short: 'ATE', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'certification', label: 'Test / Certification', short: 'CERT', color: 'bg-rose-100 text-rose-700' },
];

// ============= EVALUATIONS TAB COMPONENT =============
const EvaluationsTab = ({ readOnly = false }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [modules, setModules] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editDialog, setEditDialog] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [evRes, modRes, seqRes] = await Promise.all([
        axios.get(`${API_URL}/evaluations-manuelles`),
        axios.get(`${API_URL}/modules`),
        axios.get(`${API_URL}/sequences`).catch(() => ({ data: [] })),
      ]);
      setEvaluations(evRes.data);
      setModules(modRes.data);
      setSequences(seqRes.data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    return evaluations.filter(ev => {
      const matchSearch = !searchTerm ||
        ev.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ev.code?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchType = filterType === 'all' || ev.type_evaluation === filterType;
      return matchSearch && matchType;
    }).sort((a, b) => (a.titre || '').localeCompare(b.titre || '', 'fr', { sensitivity: 'base' }));
  }, [evaluations, searchTerm, filterType]);

  const getModuleName = (moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    return mod ? `${mod.code} - ${mod.titre}` : '';
  };

  const openNew = () => {
    setEditingEval({
      titre: '',
      type_evaluation: 'exercice',
      objectif: '',
      module_id: '',
      sequence_code: '',
      sequence_name: '',
      competences_visees: [],
      statut: 'brouillon',
    });
    setEditDialog(true);
  };

  const openEdit = (ev) => {
    setEditingEval({ ...ev });
    setEditDialog(true);
  };

  const addCompetence = () => {
    setEditingEval(prev => ({
      ...prev,
      competences_visees: [...(prev.competences_visees || []), { id: crypto.randomUUID(), titre: '' }],
    }));
  };

  const removeCompetence = (id) => {
    setEditingEval(prev => ({
      ...prev,
      competences_visees: prev.competences_visees.filter(c => c.id !== id),
    }));
  };

  const updateCompetence = (id, titre) => {
    setEditingEval(prev => ({
      ...prev,
      competences_visees: prev.competences_visees.map(c => c.id === id ? { ...c, titre } : c),
    }));
  };

  const handleSave = async () => {
    if (!editingEval.titre.trim()) { toast.error('Le titre est obligatoire'); return; }
    if (editingEval.competences_visees.some(c => !c.titre.trim())) { toast.error('Toutes les competences doivent avoir un titre'); return; }
    setSaving(true);
    try {
      if (editingEval.id) {
        await axios.put(`${API_URL}/evaluations-manuelles/${editingEval.id}`, editingEval);
        toast.success('Evaluation mise a jour');
      } else {
        await axios.post(`${API_URL}/evaluations-manuelles`, editingEval);
        toast.success('Evaluation creee');
      }
      setEditDialog(false);
      setEditingEval(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/evaluations-manuelles/${deleteTarget.id}`);
      toast.success('Evaluation supprimee');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="evaluations-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-evaluations" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]" data-testid="filter-eval-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {EVAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="btn-new-eval" style={{ display: readOnly ? 'none' : undefined }}>
          <Plus className="w-4 h-4" /> Nouvelle evaluation
        </Button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune evaluation trouvee</p>
            <Button variant="outline" className="mt-3 gap-2" onClick={openNew} style={{ display: readOnly ? 'none' : undefined }}><Plus className="w-4 h-4" /> Creer une evaluation</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="evaluations-table">
          <p className="px-3 py-1.5 text-[11px] text-slate-500 border-b bg-slate-50">{filtered.length} évaluation(s) — tri alphabétique</p>
          <table className="w-full text-xs min-w-[720px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b">
                <th className="px-3 py-2 w-14">Type</th><th className="px-3 py-2 w-24">Code</th><th className="px-3 py-2">Titre</th><th className="px-3 py-2">Module / séquence</th><th className="px-3 py-2 w-24 text-center">Savoir-faire</th><th className="px-3 py-2 w-20">Statut</th><th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(ev => {
                const typeConf = EVAL_TYPES.find(t => t.value === ev.type_evaluation) || EVAL_TYPES[0];
                return (
                  <tr key={ev.id} className="border-b last:border-b-0 hover:bg-indigo-50/40 cursor-pointer" onClick={() => !readOnly && openEdit(ev)} data-testid={`eval-card-${ev.id}`}>
                    <td className="px-3 py-1.5"><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${typeConf.color}`} title={typeConf.label}>{typeConf.short}</span></td>
                    <td className="px-3 py-1.5 font-mono text-slate-400">{ev.code}</td>
                    <td className="px-3 py-1.5 font-medium text-slate-900">{ev.titre}{(ev.fichiers_count || 0) > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-slate-400" title={`${ev.fichiers_count} fichier(s) joint(s)`} data-testid={`eval-fichiers-count-${ev.id}`}><Paperclip className="w-3 h-3" />{ev.fichiers_count}</span>}</td>
                    <td className="px-3 py-1.5 text-slate-500">
                      {ev.sequence_code ? `${ev.sequence_code}${ev.sequence_name ? ` — ${ev.sequence_name}` : ''}` : ev.module_id ? getModuleName(ev.module_id) : ''}
                      {(ev.modules_rattaches || []).length > 0 && <span className="ml-1 inline-flex gap-1 flex-wrap" data-testid={`eval-modules-${ev.id}`}>{ev.modules_rattaches.map(m => <span key={m.id} title={m.titre} className="text-[10px] font-mono text-violet-700 bg-violet-50 px-1 py-0.5 rounded">{m.code}</span>)}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center text-slate-600"><Target className="w-3 h-3 inline mr-1 text-slate-400" />{(ev.competences_visees || []).length}</td>
                    <td className="px-3 py-1.5"><Badge variant={ev.statut === 'publie' ? 'default' : 'secondary'} className="text-[10px]">{ev.statut === 'publie' ? 'Publié' : 'Brouillon'}</Badge></td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(ev); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                        {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={(o) => { if (!o) { setEditDialog(false); setEditingEval(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEval?.id ? "Modifier l'evaluation" : 'Nouvelle evaluation'}</DialogTitle>
            <DialogDescription>Définissez le type, le titre, l'objectif et les savoir-faire / connaissances visés.</DialogDescription>
          </DialogHeader>
          {editingEval && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={editingEval.type_evaluation} onValueChange={(v) => setEditingEval(prev => ({ ...prev, type_evaluation: v }))}>
                    <SelectTrigger data-testid="eval-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Séquence de rattachement</Label>
                  <Select
                    value={editingEval.sequence_code || '_none'}
                    onValueChange={(v) => {
                      if (v === '_none') { setEditingEval(prev => ({ ...prev, sequence_code: '', sequence_name: '' })); return; }
                      const s = sequences.find(x => x.code === v);
                      setEditingEval(prev => ({ ...prev, sequence_code: v, sequence_name: s?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="eval-sequence-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="_none">Aucune</SelectItem>
                      {sequences.map(s => <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-1">Le rattachement aux modules se fait depuis la fiche du module</p>
                </div>
              </div>
              {editingEval.id && (
                <div>
                  <Label>Code (auto-genere)</Label>
                  <Input value={editingEval.code || ''} onChange={(e) => setEditingEval(prev => ({ ...prev, code: e.target.value }))} data-testid="eval-code" />
                </div>
              )}
              <div>
                <Label>Titre *</Label>
                <Input value={editingEval.titre} onChange={(e) => setEditingEval(prev => ({ ...prev, titre: e.target.value }))} placeholder="Ex: Mise en situation pratique de cariste" data-testid="eval-titre" />
              </div>
              <div>
                <Label>Objectif</Label>
                <Textarea value={editingEval.objectif || ''} onChange={(e) => setEditingEval(prev => ({ ...prev, objectif: e.target.value }))} placeholder="Decrire l'objectif de cette evaluation..." rows={3} data-testid="eval-objectif" />
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={editingEval.statut || 'brouillon'} onValueChange={(v) => setEditingEval(prev => ({ ...prev, statut: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="publie">Publie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Competences visees */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Savoir-faire et connaissances visés</Label>
                  <Button variant="outline" size="sm" onClick={addCompetence} className="gap-1" data-testid="btn-add-competence">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </Button>
                </div>
                {(editingEval.competences_visees || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Aucun savoir-faire / connaissance ajouté</p>
                ) : (
                  <div className="space-y-2">
                    {editingEval.competences_visees.map((c, i) => (
                      <div key={c.id} className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        <span className="text-xs text-slate-400 font-mono w-6">{i + 1}.</span>
                        <Input value={c.titre} onChange={(e) => updateCompetence(c.id, e.target.value)} placeholder="Savoir-faire ou connaissance..." className="flex-1" data-testid={`competence-input-${i}`} />
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => removeCompetence(c.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-slate-400">Chaque savoir-faire / connaissance sera évalué par le formateur : NA (0-40%), ECA (40-60%), A (60-100%)</p>
              </div>
              {editingEval.id && <EvalFichiersSection evalId={editingEval.id} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog(false); setEditingEval(null); }}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="btn-save-eval">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingEval?.id ? 'Mettre a jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette evaluation ?</AlertDialogTitle>
            <AlertDialogDescription>L'evaluation "{deleteTarget?.titre}" et tous ses resultats seront supprimes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


// ============= MAIN PAGE =============
const SupportPedaPage = () => {
  const [activeTab, setActiveTab] = useState('modules');
  const { user } = useAuth();
  const readOnly = user?.role === 'formateur';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="support-peda-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Support pedagogique</h1>
          <p className="text-sm text-slate-500 mt-1">Modules, QCM et evaluations manuelles</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="modules" className="gap-2" data-testid="tab-modules">
              <BookOpen className="w-4 h-4" /> Modules
            </TabsTrigger>
            <TabsTrigger value="qcm" className="gap-2" data-testid="tab-qcm-support">
              <HelpCircle className="w-4 h-4" /> QCM
            </TabsTrigger>
            <TabsTrigger value="evaluations" className="gap-2" data-testid="tab-evaluations">
              <ClipboardCheck className="w-4 h-4" /> Applications / Evaluations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="modules" className="mt-4">
            <ModulesPage embedded={true} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="qcm" className="mt-4">
            <QCMPage embedded={true} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="evaluations" className="mt-4">
            <EvaluationsTab readOnly={readOnly} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export { EvaluationsTab };
export default SupportPedaPage;
